import React, { memo, useState } from 'react';
import {
    Table,
    Checkbox,
    Text,
    Box,
    Group,
    Loader,
    ActionIcon,
    ScrollArea
} from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from '../common/EmptyState';

export interface Entity {
    Id: string;
    DisplayName?: string;
    Name?: string;
    Type?: string;
    [key: string]: any;
}

interface EntityBrowserTableProps {
    entities: Entity[];
    selectedIds: string[];
    loading: boolean;
    singleSelection: boolean;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    onEntitySelect: (entityId: string, isSelected: boolean) => void;
    onSelectAll: () => void;
    onSortChange: (field: string) => void;
    getDisplayName: (entity: Entity) => string;
    getEntityType: (entity: Entity) => string;
    alreadySelectedEntities?: Entity[];
    newlySelectedEntities?: Entity[];
    isFullscreen?: boolean;
    /** When false, hide the selection checkbox column (e.g. view-only modal). Default true. */
    showSelectionColumn?: boolean;
    /** Additional attribute names to show as columns (e.g. Role, Department). Values read from entity with Pascal or camel case. */
    extraColumnAttributes?: string[];
}

const EntityBrowserTable: React.FC<EntityBrowserTableProps> = memo(({
    entities,
    selectedIds,
    loading,
    singleSelection,
    sortBy,
    sortOrder,
    searchQuery,
    onEntitySelect,
    onSelectAll,
    onSortChange,
    getDisplayName,
    getEntityType,
    alreadySelectedEntities = [],
    newlySelectedEntities = [],
    isFullscreen = false,
    showSelectionColumn = true,
    extraColumnAttributes = []
}) => {
    const [isAlreadySelectedExpanded, setIsAlreadySelectedExpanded] = useState(true);
    const [isNewlySelectedExpanded, setIsNewlySelectedExpanded] = useState(true);

    const isAllSelected = entities.length > 0 && selectedIds.length === entities.length;
    const isIndeterminate = selectedIds.length > 0 && selectedIds.length < entities.length;

    const alreadySelectedIds = alreadySelectedEntities.map(e => e.Id);
    const alreadySelectedCount = alreadySelectedEntities.filter(e => selectedIds.includes(e.Id)).length;
    const previouslySelectedCount = alreadySelectedEntities.length - alreadySelectedCount;
    const newlySelectedNotInView = newlySelectedEntities.filter(e => !entities.some(ent => ent.Id === e.Id));
    const newlySelectedCount = newlySelectedNotInView.filter(e => selectedIds.includes(e.Id)).length;

    const tableContainerStyle: React.CSSProperties = {
        maxHeight: isFullscreen ? '100%' : 'calc(100vh - 400px)',
        minHeight: isFullscreen ? 0 : 400,
        height: isFullscreen ? '100%' : undefined,
        flex: isFullscreen ? 1 : undefined,
        display: isFullscreen ? 'flex' : undefined,
        flexDirection: isFullscreen ? 'column' : undefined,
        overflow: 'auto'
    };

    const headerCellStyle: React.CSSProperties = { cursor: 'pointer', userSelect: 'none', padding: '8px 16px' };
    const rowHoverStyle = (isSelected: boolean) => ({
        backgroundColor: isSelected ? 'var(--mantine-color-blue-light)' : undefined,
        opacity: isSelected ? 0.8 : 1,
        cursor: 'pointer'
    });

    const showSelector = !singleSelection && showSelectionColumn;
    const extraCount = extraColumnAttributes.length;
    const colSpan = (showSelector ? 1 : 0) + 3 + extraCount;

    const getEntityAttrValue = (entity: Entity, attr: string): any => {
        const key = attr;
        if (entity[key] !== undefined && entity[key] !== null) return entity[key];
        const pascal = attr.charAt(0).toUpperCase() + attr.slice(1);
        return entity[pascal];
    };
    const formatCellValue = (value: any): string => {
        if (value == null) return '—';
        if (typeof value === 'object') return Array.isArray(value) ? value.map(String).join(', ') : JSON.stringify(value);
        return String(value);
    };

    return (
        <ScrollArea style={tableContainerStyle}>
            <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                <Table striped highlightOnHover stickyHeader>
                    <Table.Thead>
                        <Table.Tr>
                            {showSelector && (
                                <Table.Th w={48}>
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={onSelectAll}
                                        disabled={loading || entities.length === 0}
                                    />
                                </Table.Th>
                            )}
                            <Table.Th style={headerCellStyle} onClick={() => onSortChange('DisplayName')}>
                                <Group gap="xs" align="center">
                                    <Text size="sm" fw={600}>Display Name</Text>
                                    {sortBy === 'DisplayName' && (
                                        <Text size="xs">{sortOrder === 'asc' ? '↑' : '↓'}</Text>
                                    )}
                                </Group>
                            </Table.Th>
                            <Table.Th style={headerCellStyle} onClick={() => onSortChange('Type')}>
                                <Group gap="xs" align="center">
                                    <Text size="sm" fw={600}>Type</Text>
                                    {sortBy === 'Type' && (
                                        <Text size="xs">{sortOrder === 'asc' ? '↑' : '↓'}</Text>
                                    )}
                                </Group>
                            </Table.Th>
                            <Table.Th style={headerCellStyle} onClick={() => onSortChange('Id')}>
                                <Group gap="xs" align="center">
                                    <Text size="sm" fw={600}>ID</Text>
                                    {sortBy === 'Id' && (
                                        <Text size="xs">{sortOrder === 'asc' ? '↑' : '↓'}</Text>
                                    )}
                                </Group>
                            </Table.Th>
                            {extraColumnAttributes.map((attr) => (
                                <Table.Th key={attr} px="md" py="sm">
                                    <Text size="sm" fw={600}>{attr}</Text>
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {loading ? (
                            <Table.Tr>
                                <Table.Td colSpan={colSpan} ta="center" py="xl">
                                    <Loader size="sm" />
                                </Table.Td>
                            </Table.Tr>
                        ) : entities.length === 0 && alreadySelectedEntities.length === 0 && newlySelectedNotInView.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={colSpan}>
                                    <EmptyState
                                        title={searchQuery ? 'No entities found matching your search' : 'No entities available'}
                                        description={searchQuery ? 'Try broadening your search criteria.' : undefined}
                                    />
                                </Table.Td>
                            </Table.Tr>
                        ) : (
                            <>
                                {alreadySelectedEntities.length > 0 && (
                                    <>
                                        <Table.Tr bg="var(--mantine-color-default-hover)">
                                            <Table.Td colSpan={colSpan} p="sm">
                                                <Group justify="space-between" align="center">
                                                    <Group gap="xs" align="center">
                                                        <ActionIcon
                                                            variant="subtle"
                                                            size="sm"
                                                            onClick={() => setIsAlreadySelectedExpanded(!isAlreadySelectedExpanded)}
                                                        >
                                                            {isAlreadySelectedExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                                        </ActionIcon>
                                                        <Text size="sm" fw={600}>
                                                            Already Selected Entities ({alreadySelectedEntities.length})
                                                        </Text>
                                                        {!isAlreadySelectedExpanded && (
                                                            <Text size="xs" c="dimmed" ml="xs">
                                                                {alreadySelectedCount > 0 && `(${alreadySelectedCount} selected`}
                                                                {alreadySelectedCount > 0 && previouslySelectedCount > 0 && ', '}
                                                                {previouslySelectedCount > 0 && `${previouslySelectedCount} previously selected`}
                                                                {alreadySelectedCount > 0 && ')'}
                                                                {alreadySelectedCount === 0 && previouslySelectedCount > 0 && `(${previouslySelectedCount} previously selected)`}
                                                            </Text>
                                                        )}
                                                    </Group>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                        {isAlreadySelectedExpanded && alreadySelectedEntities.map((entity) => {
                                            const isSelected = selectedIds.includes(entity.Id);
                                            return (
                                                <Table.Tr
                                                    key={`already-${entity.Id}`}
                                                    onClick={() => onEntitySelect(entity.Id, !isSelected)}
                                                    style={rowHoverStyle(isSelected)}
                                                >
                                                    {showSelector && (
                                                        <Table.Td w={48} onClick={(e) => e.stopPropagation()}>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    onEntitySelect(entity.Id, e.currentTarget.checked);
                                                                }}
                                                            />
                                                        </Table.Td>
                                                    )}
                                                    <Table.Td py={4} px="md">
                                                        <Group gap="xs" align="center">
                                                            <Text size="sm">{getDisplayName(entity)}</Text>
                                                            <Text size="xs" c={isSelected ? 'blue' : 'dimmed'} fs="italic">
                                                                {isSelected ? '(already selected)' : '(previously selected)'}
                                                            </Text>
                                                        </Group>
                                                    </Table.Td>
                                                    <Table.Td py={4} px="md">
                                                        <Text size="sm" c="dimmed">{getEntityType(entity)}</Text>
                                                    </Table.Td>
                                                    <Table.Td py={4} px="md">
                                                        <Text size="sm" c="dimmed" ff="monospace">{entity.Id}</Text>
                                                    </Table.Td>
                                                    {extraColumnAttributes.map((attr) => (
                                                        <Table.Td key={attr} py={4} px="md">
                                                            <Text size="sm" c="dimmed">{formatCellValue(getEntityAttrValue(entity, attr))}</Text>
                                                        </Table.Td>
                                                    ))}
                                                </Table.Tr>
                                            );
                                        })}
                                    </>
                                )}

                                {newlySelectedNotInView.length > 0 && (
                                    <>
                                        <Table.Tr bg="var(--mantine-color-default-hover)">
                                            <Table.Td colSpan={colSpan} p="sm">
                                                <Group justify="space-between" align="center">
                                                    <Group gap="xs" align="center">
                                                        <ActionIcon
                                                            variant="subtle"
                                                            size="sm"
                                                            onClick={() => setIsNewlySelectedExpanded(!isNewlySelectedExpanded)}
                                                        >
                                                            {isNewlySelectedExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                                        </ActionIcon>
                                                        <Text size="sm" fw={600}>
                                                            Selected in This Session ({newlySelectedNotInView.length})
                                                        </Text>
                                                        {!isNewlySelectedExpanded && newlySelectedCount > 0 && (
                                                            <Text size="xs" c="dimmed" ml="xs">
                                                                ({newlySelectedCount} selected)
                                                            </Text>
                                                        )}
                                                    </Group>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                        {isNewlySelectedExpanded && newlySelectedNotInView.map((entity) => {
                                            const isSelected = selectedIds.includes(entity.Id);
                                            return (
                                                <Table.Tr
                                                    key={`newly-${entity.Id}`}
                                                    onClick={() => onEntitySelect(entity.Id, !isSelected)}
                                                    style={rowHoverStyle(isSelected)}
                                                >
                                                    {showSelector && (
                                                        <Table.Td w={48} onClick={(e) => e.stopPropagation()}>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    onEntitySelect(entity.Id, e.currentTarget.checked);
                                                                }}
                                                            />
                                                        </Table.Td>
                                                    )}
                                                    <Table.Td py={4} px="md">
                                                        <Group gap="xs" align="center">
                                                            <Text size="sm">{getDisplayName(entity)}</Text>
                                                            <Text size="xs" c="blue" fs="italic">(selected)</Text>
                                                        </Group>
                                                    </Table.Td>
                                                    <Table.Td py={4} px="md">
                                                        <Text size="sm" c="dimmed">{getEntityType(entity)}</Text>
                                                    </Table.Td>
                                                    <Table.Td py={4} px="md">
                                                        <Text size="sm" c="dimmed" ff="monospace">{entity.Id}</Text>
                                                    </Table.Td>
                                                    {extraColumnAttributes.map((attr) => (
                                                        <Table.Td key={attr} py={4} px="md">
                                                            <Text size="sm" c="dimmed">{formatCellValue(getEntityAttrValue(entity, attr))}</Text>
                                                        </Table.Td>
                                                    ))}
                                                </Table.Tr>
                                            );
                                        })}
                                    </>
                                )}

                                {(alreadySelectedEntities.length > 0 || newlySelectedNotInView.length > 0) && entities.length > 0 && (
                                    <Table.Tr>
                                        <Table.Td colSpan={colSpan} p="sm" style={{ borderBottom: '2px solid var(--mantine-color-default-border)' }}>
                                            <Text size="xs" c="dimmed" fw={600}>Available Entities</Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                                {entities.map((entity) => {
                                    const isSelected = selectedIds.includes(entity.Id);
                                    const isAlreadySelected = alreadySelectedIds.includes(entity.Id);
                                    const isNewlySelected = newlySelectedNotInView.some(e => e.Id === entity.Id);
                                    if (isAlreadySelected || isNewlySelected) return null;

                                    return (
                                        <Table.Tr
                                            key={entity.Id}
                                            onClick={() => onEntitySelect(entity.Id, !isSelected)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {showSelector && (
                                                <Table.Td w={48} onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            onEntitySelect(entity.Id, e.currentTarget.checked);
                                                        }}
                                                    />
                                                </Table.Td>
                                            )}
                                            <Table.Td py={4} px="md">
                                                <Text size="sm">{getDisplayName(entity)}</Text>
                                            </Table.Td>
                                            <Table.Td py={4} px="md">
                                                <Text size="sm" c="dimmed">{getEntityType(entity)}</Text>
                                            </Table.Td>
                                            <Table.Td py={4} px="md">
                                                <Text size="sm" c="dimmed" ff="monospace">{entity.Id}</Text>
                                            </Table.Td>
                                            {extraColumnAttributes.map((attr) => (
                                                <Table.Td key={attr} py={4} px="md">
                                                    <Text size="sm" c="dimmed">{formatCellValue(getEntityAttrValue(entity, attr))}</Text>
                                                </Table.Td>
                                            ))}
                                        </Table.Tr>
                                    );
                                })}
                            </>
                        )}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        </ScrollArea>
    );
});

EntityBrowserTable.displayName = 'EntityBrowserTable';

export default EntityBrowserTable;
